import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { ChevronDown, Heart, Eye, Clock, Sparkles, Users, ArrowRight, Gamepad2, BookOpen, Target, MessageCircle, Brain, Bell, X, ShoppingBag, ShoppingCart } from 'lucide-react';
import axios from 'axios';
import OnboardingTutorial from './OnboardingTutorial';
import Testimonials from './Testimonials';
import LiveStatsCounter from './LiveStatsCounter';
import DarkModeToggle from './DarkModeToggle';
import NotificationPanel from './NotificationPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [showDropdown, setShowDropdown] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userResults, setUserResults] = useState([]);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [communityPreview, setCommunityPreview] = useState(null);
  const [communityQuestions, setCommunityQuestions] = useState([]);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    // Show welcome popup on first visit
    const hasSeenWelcome = sessionStorage.getItem('luvcup_welcome_shown');
    if (!hasSeenWelcome) {
      setShowWelcomePopup(true);
      
      // Auto-dismiss after 6 seconds
      const timer = setTimeout(() => {
        setShowWelcomePopup(false);
        sessionStorage.setItem('luvcup_welcome_shown', 'true');
      }, 6000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    // Check if user has seen onboarding
    const hasSeenOnboarding = localStorage.getItem('luvcup_onboarding_completed');
    if (!hasSeenOnboarding && user) {
      setShowOnboarding(true);
    }
    
    // Load user results if logged in
    if (user) {
      loadUserResults();
      loadCartCount();
    }
  }, [user]);

  const loadCartCount = async () => {
    if (!user || !token) return;
    try {
      const response = await axios.get(`${API}/store/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartCount(response.data.items?.length || 0);
    } catch (error) {
      console.error('Failed to load cart:', error);
    }
  };
  
  const loadUserResults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserResults(response.data);
    } catch (error) {
      console.error('Failed to load user results:', error);
    }
  };

  useEffect(() => {
    const fetchQuizzes = async () => {
      try {
        const response = await axios.get(`${API}/quizzes`);
        setQuizzes(response.data.tests);
      } catch (error) {
        console.error('Failed to fetch quizzes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuizzes();
    fetchCommunityPreview();
  }, []);

  const fetchCommunityPreview = async () => {
    try {
      const response = await axios.get(`${API}/community/questions`);
      if (response.data.questions && response.data.questions.length > 0) {
        // Store all questions for the additional list
        setCommunityQuestions(response.data.questions.slice(0, 5)); // Show up to 5 questions
        
        // Get the most recent question with comments for the main preview
        const questionWithComments = response.data.questions.find(q => q.comments && q.comments.length >= 3);
        if (questionWithComments) {
          setCommunityPreview(questionWithComments);
        } else if (response.data.questions[0]) {
          // Fallback to first question even if it has fewer comments
          setCommunityPreview(response.data.questions[0]);
        }
      } else {
        // Show sample data if no questions exist
        setCommunityPreview({
          author_name: "Sarah M.",
          question: "How do you navigate different love languages in a relationship? My partner and I seem to express love so differently!",
          created_at: new Date().toISOString(),
          comments: [
            {
              author_name: "Mike R.",
              comment: "I had the same issue! We took the love languages quiz together and discussed our results. It really helped us understand each other better.",
              created_at: new Date().toISOString()
            },
            {
              author_name: "Jennifer L.",
              comment: "Communication is key! We have a weekly check-in where we talk about what made us feel loved that week. It's been a game changer for us.",
              created_at: new Date().toISOString()
            },
            {
              author_name: "David K.",
              comment: "Try the 'love language challenge' - each week, focus on your partner's love language. It's fun and eye-opening!",
              created_at: new Date().toISOString()
            }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to fetch community preview:', error);
      // Show sample data on error too
      setCommunityPreview({
        author_name: "Sarah M.",
        question: "How do you navigate different love languages in a relationship? My partner and I seem to express love so differently!",
        created_at: new Date().toISOString(),
        comments: [
          {
            author_name: "Mike R.",
            comment: "I had the same issue! We took the love languages quiz together and discussed our results. It really helped us understand each other better.",
            created_at: new Date().toISOString()
          },
          {
            author_name: "Jennifer L.",
            comment: "Communication is key! We have a weekly check-in where we talk about what made us feel loved that week. It's been a game changer for us.",
            created_at: new Date().toISOString()
          },
          {
            author_name: "David K.",
            comment: "Try the 'love language challenge' - each week, focus on your partner's love language. It's fun and eye-opening!",
            created_at: new Date().toISOString()
          }
        ]
      });
    }
  };

  useEffect(() => {
    // Handle scrolling to section after page load
    const hash = window.location.hash;
    if (hash === '#start-journey') {
      setTimeout(() => {
        const element = document.getElementById('start-journey');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500); // Delay to ensure page is fully loaded
    }
  }, []);

  const getTestIcon = (testId) => {
    const icons = {
      'test-1': <Heart className="w-6 h-6" />,
      'test-2': <Eye className="w-6 h-6" />,
      'test-3': <Clock className="w-6 h-6" />,
      'test-4': <Sparkles className="w-6 h-6" />,
      'test-5': <Users className="w-6 h-6" />
    };
    return icons[testId] || <Heart className="w-6 h-6" />;
  };

  const handleQuizSelect = (testId) => {
    if (user) {
      navigate(`/quiz/${testId}`);
    } else {
      navigate('/auth');
    }
    setShowDropdown(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50">
      {/* Onboarding Tutorial */}
      {showOnboarding && (
        <OnboardingTutorial 
          onClose={() => setShowOnboarding(false)}
          onComplete={() => setShowOnboarding(false)}
        />
      )}

      {/* Welcome Popup Message */}
      {showWelcomePopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 animate-fade-in">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowWelcomePopup(false);
              sessionStorage.setItem('luvcup_welcome_shown', 'true');
            }}
          ></div>
          
          {/* Popup Card */}
          <div className="relative bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 rounded-2xl shadow-2xl p-8 max-w-md w-full border-2 border-rose-200 animate-zoom-in">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowWelcomePopup(false);
                sessionStorage.setItem('luvcup_welcome_shown', 'true');
              }}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-full transition-all"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Heart Icon */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                <Heart className="w-20 h-20 text-rose-500 fill-rose-500 animate-heart-beat" />
                <Sparkles className="w-8 h-8 text-yellow-400 absolute -top-2 -right-2 animate-pulse" />
              </div>
            </div>

            {/* Message */}
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold gradient-text mb-4 leading-tight">
                Does Love Have A Price?
              </h2>
              
              <p className="text-gray-600 text-sm">
                Welcome to LuvCup - Where Emotional Intelligence Meets Love ✨
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 rounded-full animate-progress-6s"
                ></div>
              </div>
              <p className="text-xs text-center text-gray-500 mt-2">Auto-closing in 6 seconds...</p>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Top Bar - Logo, Premium, Login/Logout, and Dark Mode */}
      <nav className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 shadow-md px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <Heart className="w-6 h-6 text-rose-600" />
            <span className="text-xl font-bold gradient-text">LuvCup</span>
          </div>

          {/* Premium, Login/Logout, Dark Mode */}
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => navigate('/premium')}
              className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-xs font-semibold rounded-full hover:shadow-lg transition-all"
            >
              <span>👑</span>
              <span>Premium</span>
            </button>
            
            {user ? (
              <button 
                onClick={() => {
                  localStorage.removeItem('token');
                  window.location.href = '/';
                }}
                className="text-xs text-gray-600 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400 font-medium transition-colors"
              >
                Logout
              </button>
            ) : (
              <button 
                onClick={() => navigate('/auth')}
                className="btn-secondary text-xs px-3 py-1.5"
              >
                Login
              </button>
            )}
            
            <DarkModeToggle />
          </div>
        </div>
      </nav>

      {/* Desktop Navigation - Hidden on Mobile, Visible on Desktop */}
      <nav className="hidden md:block fixed top-0 left-0 right-0 z-50 glass-card mx-2 sm:mx-6 mt-2 sm:mt-4 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex flex-wrap justify-between items-center gap-2">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-rose-600" />
            <span className="text-xl sm:text-2xl font-bold gradient-text">LuvCup</span>
          </div>

          {/* Desktop & Tablet Navigation - Full Menu */}
          <div className="flex flex-wrap items-center gap-1.5 lg:gap-2">
            <DarkModeToggle />
            {user && (
              <>
                <button 
                  onClick={() => navigate('/courses')}
                  className="flex items-center space-x-1 px-2 xl:px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white text-xs xl:text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                  data-testid="courses-link"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Courses</span>
                </button>
                <button 
                  onClick={() => navigate('/emotions')}
                  className="flex items-center space-x-1 px-2 xl:px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-xs xl:text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                  data-testid="emotions-link"
                >
                  <Brain className="w-4 h-4" />
                  <span>Emotions</span>
                </button>
              </>
            )}
            {user && (
              <>
                <button 
                  onClick={() => navigate('/community')}
                  className="flex items-center space-x-1 px-2 xl:px-3 py-2 bg-gradient-to-r from-green-500 to-teal-600 text-white text-xs xl:text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                  data-testid="community-link"
                >
                  <Users className="w-4 h-4" />
                  <span>Community</span>
                </button>
                <button 
                  onClick={() => navigate('/couples')}
                  className="flex items-center space-x-1 px-2 xl:px-3 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white text-xs xl:text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                  data-testid="couples-link"
                >
                  <Heart className="w-4 h-4" />
                  <span>Couples</span>
                </button>
                <button 
                  onClick={() => navigate('/challenges')}
                  className="flex items-center space-x-1 px-2 xl:px-3 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-xs xl:text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                  data-testid="challenges-link"
                >
                  <Target className="w-4 h-4" />
                  <span>Challenges</span>
                </button>
              </>
            )}
            <button 
              onClick={() => navigate('/premium')}
              className="flex items-center space-x-1 px-2 xl:px-3 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-xs xl:text-sm font-semibold rounded-full hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="premium-btn"
            >
              <span className="text-base xl:text-lg">👑</span>
              <span>Premium</span>
            </button>
            <button 
              onClick={() => navigate('/store')}
              className="flex items-center space-x-1 px-2 xl:px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400 transition-colors text-xs xl:text-sm font-medium"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden lg:inline">Store</span>
            </button>
            {user && (
              <button 
                onClick={() => navigate('/store')}
                className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-rose-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            {user ? (
              <>
                {userResults.length >= 2 && (
                  <button 
                    onClick={() => navigate('/games')}
                    className="flex items-center space-x-1 px-2 xl:px-3 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-xs xl:text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                    data-testid="games-link"
                  >
                    <Gamepad2 className="w-4 h-4" />
                    <span>Games</span>
                  </button>
                )}
                <NotificationPanel />
                <button 
                  onClick={() => navigate('/messages')}
                  className="flex items-center space-x-1 px-2 xl:px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400 transition-colors text-xs xl:text-sm font-medium"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="hidden lg:inline">Messages</span>
                </button>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="text-gray-600 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400 font-medium transition-colors text-xs xl:text-sm"
                  data-testid="dashboard-link"
                >
                  Dashboard
                </button>
                {user.is_admin && (
                  <button 
                    onClick={() => navigate('/admin')}
                    className="text-gray-600 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400 font-medium transition-colors text-xs xl:text-sm"
                    data-testid="admin-link"
                  >
                    Admin
                  </button>
                )}
                <span className="text-gray-600 dark:text-gray-300 text-xs xl:text-sm hidden lg:inline">Hi, {user.full_name?.split(' ')[0]}</span>
                <button 
                  onClick={() => {
                    localStorage.removeItem('token');
                    window.location.href = '/';
                  }}
                  className="text-sm text-gray-600 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400 font-medium transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <button 
                onClick={() => navigate('/auth')}
                className="btn-secondary text-xs xl:text-sm px-3 xl:px-4 py-2"
                data-testid="login-btn"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex items-center justify-center px-6 pt-24 sm:pt-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Floating hearts animation */}
          <div className="floating-heart" style={{ top: '20%', left: '10%', animationDelay: '0s' }}>💕</div>
          <div className="floating-heart" style={{ top: '30%', right: '15%', animationDelay: '2s' }}>💖</div>
          <div className="floating-heart" style={{ bottom: '25%', left: '20%', animationDelay: '4s' }}>💝</div>
          <div className="floating-heart" style={{ bottom: '35%', right: '25%', animationDelay: '1s' }}>💘</div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold gradient-text hero-title mb-6 fade-in mt-8 sm:mt-4 md:mt-0">
            Discover Your
            <br />Love Language
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto fade-in">
            Explore your relationships and emotional intelligence through interactive tests, 
            personalized evaluations, and AI-powered insights.
          </p>

          {/* Dropdown Quiz Selector */}
          <div className="relative inline-block mb-8 fade-in w-full max-w-md mx-auto">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="w-full px-8 py-4 text-lg font-semibold bg-blue-500 hover:bg-blue-600 text-white dark:bg-pink-500 dark:hover:bg-pink-600 transition-all duration-300 flex items-center justify-center space-x-3 rounded-2xl shadow-lg hover:shadow-xl"
              data-testid="quiz-selector-btn"
            >
              <span>Choose Your Love Test</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showDropdown && (
              <>
                {/* Mobile backdrop overlay */}
                <div 
                  className="fixed inset-0 bg-black bg-opacity-30 z-40 md:hidden"
                  onClick={() => setShowDropdown(false)}
                  data-testid="dropdown-backdrop"
                ></div>
                
                {/* Dropdown content */}
                <div className="absolute top-full left-0 right-0 mt-2 dropdown-menu p-3 z-50 max-h-96 overflow-y-auto md:relative md:max-w-none" data-testid="quiz-dropdown">
                  {/* Close button for mobile */}
                  <div className="flex justify-between items-center mb-3 md:hidden">
                    <h3 className="text-lg font-semibold text-gray-800">Choose Your Test</h3>
                    <button
                      onClick={() => setShowDropdown(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                      data-testid="close-dropdown-btn"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="spinner mx-auto"></div>
                    </div>
                  ) : (
                    <div className="space-y-2 md:space-y-3">
                      {quizzes.map((quiz, index) => (
                        <button
                          key={quiz.id}
                          onClick={() => handleQuizSelect(quiz.id)}
                          className="w-full p-3 md:p-4 text-left hover:bg-rose-50 rounded-xl transition-all duration-200 flex items-start space-x-3 md:space-x-4 border border-transparent hover:border-rose-200 hover:shadow-md"
                          data-testid={`quiz-option-${quiz.id}`}
                        >
                          <div className="text-rose-600 mt-1 flex-shrink-0">
                            <div className="w-4 h-4 md:w-6 md:h-6">
                              {getTestIcon(quiz.id)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-1 md:mb-2">
                              <div className="test-number bg-rose-100 text-rose-700 text-xs font-semibold px-2 py-1 rounded-full">
                                Test {index + 1}
                              </div>
                              <ArrowRight className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                            </div>
                            <h3 className="font-semibold text-gray-800 mb-1 md:mb-2 text-sm md:text-base leading-tight">{quiz.name}</h3>
                            <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3 leading-relaxed">{quiz.description}</p>
                            <div className="test-badges flex items-center space-x-2 md:space-x-3 text-xs">
                              <span className="bg-blue-50 text-blue-600 font-medium px-2 py-1 rounded-full text-xs">{quiz.question_count} questions</span>
                              <span className="text-gray-500 text-xs">3-5 min</span>
                              <span className="bg-purple-50 text-purple-600 font-medium px-2 py-1 rounded-full text-xs">AI Insights</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {!user && (
            <div className="fade-in">
              <p className="text-gray-600 mb-4">Ready to start your journey?</p>
              <button 
                onClick={() => navigate('/auth')}
                className="btn-primary"
                data-testid="get-started-btn"
              >
                Get Started Free
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center gradient-text mb-16">
            Why Choose LuvCup?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-card p-8 text-center slide-in-left" data-testid="feature-ai-insights">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">Personalized Insights</h3>
              <p className="text-gray-600">
                Discover your unique love patterns and relationship style with tailored analysis just for you.
              </p>
            </div>

            <div className="glass-card p-8 text-center fade-in" data-testid="feature-comprehensive">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">Comprehensive Tests</h3>
              <p className="text-gray-600">
                Explore 5 unique aspects of love - from defining love to understanding family bonds.
              </p>
            </div>

            <div className="glass-card p-8 text-center slide-in-right" data-testid="feature-reports">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Gamepad2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">Love Games</h3>
              <p className="text-gray-600">
                Play fun, interactive games designed to deepen connection and spark meaningful conversations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Live Stats Counter */}
      <LiveStatsCounter />

      {/* Community Preview Section */}
      {communityPreview && (
        <section className="py-20 px-6 bg-gradient-to-br from-purple-50 to-pink-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
                Join the Conversation
              </h2>
              <p className="text-xl text-gray-600">
                See what our community is talking about
              </p>
            </div>

            {/* Community Question Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
              {/* Question Header */}
              <div className="flex items-start space-x-4 mb-6">
                {communityPreview.user_profile_picture ? (
                  <img 
                    src={communityPreview.user_profile_picture} 
                    alt={communityPreview.user_name || communityPreview.author_name}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-rose-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0" style={{display: communityPreview.user_profile_picture ? 'none' : 'flex'}}>
                  <span className="text-white font-bold text-lg">
                    {(communityPreview.user_name || communityPreview.author_name)?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1">
                  <button
                    onClick={() => {
                      if (!user) {
                        alert('Please login to view user profiles and send messages');
                        navigate('/auth');
                      } else {
                        const userId = communityPreview.user_id || communityPreview.author_id;
                        if (userId) {
                          navigate(`/messages?user=${userId}`);
                        } else {
                          alert('Unable to send message - user ID not available');
                        }
                      }
                    }}
                    className="font-bold text-gray-800 text-xl mb-2 hover:text-rose-600 transition-colors cursor-pointer"
                  >
                    {communityPreview.user_name || communityPreview.author_name || 'Community Member'}
                  </button>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {communityPreview.question}
                  </p>
                  <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                    <span>{new Date(communityPreview.created_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{communityPreview.comments?.length || 0} comments</span>
                  </div>
                </div>
              </div>

              {/* Comments Preview */}
              {communityPreview.comments && communityPreview.comments.length > 0 && (
                <div className="border-t border-gray-200 pt-6 space-y-4">
                  {communityPreview.comments.slice(0, 3).map((comment, index) => (
                    <div key={index} className="flex items-start space-x-3 pl-8">
                      {comment.user_profile_picture ? (
                        <img 
                          src={comment.user_profile_picture} 
                          alt={comment.user_name || comment.author_name}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-blue-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0" style={{display: comment.user_profile_picture ? 'none' : 'flex'}}>
                        <span className="text-white font-semibold text-sm">
                          {(comment.user_name || comment.author_name)?.[0]?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-lg p-4">
                        <button
                          onClick={() => {
                            if (!user) {
                              alert('Please login to view user profiles and send messages');
                              navigate('/auth');
                            } else {
                              const userId = comment.user_id || comment.author_id;
                              if (userId) {
                                navigate(`/messages?user=${userId}`);
                              } else {
                                alert('Unable to send message - user ID not available');
                              }
                            }
                          }}
                          className="font-semibold text-gray-800 mb-1 hover:text-rose-600 transition-colors cursor-pointer"
                        >
                          {comment.user_name || comment.author_name || 'Anonymous'}
                        </button>
                        <p className="text-gray-700">
                          {comment.comment}
                        </p>
                        <div className="text-xs text-gray-500 mt-2">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* View More Button */}
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <button
                  onClick={() => navigate('/community')}
                  className="inline-flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-rose-500 to-purple-600 text-white rounded-full font-semibold hover:shadow-lg transition-all"
                >
                  <span>Join the Community</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <p className="text-center text-gray-600 mt-6">
              💬 Ask questions, share experiences, and connect with others on their love journey
            </p>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <Testimonials />

      {/* CTA Section */}
      <section id="start-journey" className="py-20 px-6 bg-gradient-to-br from-rose-50 to-teal-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-6">
            Start Your Love Journey Today
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of people discovering their love language and building stronger relationships.
          </p>
          {!user ? (
            <button 
              onClick={() => navigate('/auth')}
              className="btn-primary text-lg px-12 py-4"
              data-testid="cta-get-started-btn"
            >
              Get Started Free
            </button>
          ) : (
            <button 
              onClick={() => setShowDropdown(true)}
              className="btn-primary text-lg px-12 py-4"
              data-testid="cta-take-test-btn"
            >
              Take a Test Now
            </button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <Heart className="w-8 h-8 text-rose-600" />
              <span className="text-2xl font-bold gradient-text">LuvCup</span>
            </div>
            <p className="text-gray-600 mb-4">
              Love & Relationship Exploration Platform
            </p>
          </div>

          {/* Footer Links */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Product</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/store')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Store
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/about')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    About LuvCup
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Dashboard
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/couples')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Couples Mode
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Support</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/faq')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    FAQ
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/contact')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Contact Us
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/commission-apply')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Commission Program ❤️
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/privacy')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Terms of Service
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Connect</h3>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:support@luvcup.app" className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    support@luvcup.app
                  </a>
                </li>
                <li>
                  <a href="mailto:info@luvcup.app" className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    info@luvcup.app
                  </a>
                </li>
                <li>
                  <button onClick={() => navigate('/members-only')} className="text-gray-600 hover:text-rose-600 transition-colors text-sm">
                    Members Only
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-200 pt-6 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500 mb-3 md:mb-0">
              © 2025 LuvCup. Made with ❤️ for better relationships.
            </p>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin-login')}
                className="text-xs text-gray-400 hover:text-rose-600 transition-colors"
              >
                Admin Access
              </button>
              <button
                onClick={() => navigate('/admin2-login')}
                className="text-xs text-gray-400 hover:text-red-600 transition-colors"
              >
                Super Admin
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;